import { ActionButton, Content, Dialog, Flex, Heading, Item, Picker, ProgressCircle, Text, TextField, ToastQueue, View } from '@adobe/react-spectrum';
import { Upload } from 'react-bootstrap-icons';
import moment from 'moment';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDependency } from '../../../../contexts/DependencyProvider';
import { CreateIssueRequest } from '../../../../services/soap/scheduler/requests/CreateIssueRequest';
import { GetIssueRequest } from '../../../../services/soap/scheduler/requests/GetIssueRequest';
import { UpdateIssueRequest } from '../../../../services/soap/scheduler/requests/UpdateIssueRequest';
import IssueCustomFormView, { IIssueCustomFormViewHandle } from './IssueCustomFormView';
import CreateIssueDocumentUploadDialog from './CreateIssueDocumentUploadDialog';
import styles from './create_issue_dialog.module.css';
import { ContextSelection } from '../../../../components/protected/Search/ContextSelection';
import type { ContextObject } from '../../../../components/protected/Search/ContextSelection/types';
import { usePreloadAssets } from '../../../../hooks/UsePreloadAssets';

type IssueTemplate = {
  id: string;
  acl?: string;
  name: string;
  taskTitle?: string;
  typeOfWork?: string;
  status?: string;
  priority?: string;
  reminder?: string;
  solver?: string;
  solverName?: string;
  repeat?: {
    repeatMode?: string;
    validFrom?: string;
    validTo?: string;
    repeatEvery?: string;
    repeatDay?: string;
    repeatMonth?: string;
    repeatWeekDay?: string;
    repeatWeekCount?: string;
    repeatWeekDayList?: string;
    timeFrom?: string;
    timeTo?: string;
    useOffset?: string;
    offset?: string;
    limit?: string;
  };
};

interface ICreateIssueDialogProps {
  template: IssueTemplate;
  issueTypes: Array<{ id: string; name: string; formId?: string; formName?: string }>;
  statusList: Array<{ ident: string; title: string }>;
  users: Array<{ id: string; name: string }>;
  context?: {
    activityId?: string;
    projectId?: string;
    folderId?: string;
  };
  onClose: () => void;
  onCreated?: (issue: { ident: string }) => Promise<void> | void;
}

const CreateIssueDialog: React.FC<ICreateIssueDialogProps> = ({ template, issueTypes, statusList, users, context, onClose, onCreated }) => {
  const { t } = useTranslation('issue');
  const _t = (key: string) => t(`issue_detail.${key}`, { ns: 'issue' });
  const _tt = (key: string) => t(`issue_common.${key}`, { ns: 'issue' });
  const _m = (key: string) => t(`issue_messages.${key}`, { ns: 'issue' });
  const { store, schedulerService } = useDependency();
  const { folderStatusMapping, projectStatusMapping, activityStatusMapping } =
    usePreloadAssets();
  const customFormRef = useRef<IIssueCustomFormViewHandle>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [docNo, setDocNo] = useState('');
  const [sendTo, setSendTo] = useState<string | undefined>(String(store.UserId || '') || undefined);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [pendingDocuments, setPendingDocuments] = useState<Array<{ documentId: string; name: string }>>([]);
  const [issueContextSelection, setIssueContextSelection] =
    useState<ContextObject | null>(null);
  const [contextIds, setContextIds] = useState<{
    folderId: string;
    projectId: string;
    activityId: string;
  }>(() => ({
    folderId: String(context?.folderId || ''),
    projectId: String(context?.projectId || ''),
    activityId: String(context?.activityId || ''),
  }));

  const typeSettings = useMemo(
    () => issueTypes.find((item) => String(item.id) === String(template.typeOfWork || '')),
    [issueTypes, template.typeOfWork]
  );

  const formId = String(typeSettings?.formId || '').trim();
  const displayTitle = template.taskTitle || template.name || t('issue_template_picker.new_issue', { ns: 'issue' });
  const sendToOptions = users.map((user) => ({ id: String(user.id), name: user.name }));
  const contextOptions: Array<{ id: string; name: string }> = [];
  const getContextIdsFromSelection = useCallback(
    (selection: ContextObject | null) => {
      const next = {
        activityId: '',
        projectId: '',
        folderId: '',
      };

      if (!selection) return next;

      selection.pathSegmentContexts?.forEach((segment) => {
        const id = String(segment.id ?? '').trim();
        const type = String(segment.type ?? '').toUpperCase();
        if (!id) return;
        if (type === 'FOLDER') next.folderId = id;
        if (type === 'PROJECT') next.projectId = id;
        if (type === 'ACTIVITY') next.activityId = id;
      });

      const selectedId = String(selection.contextId ?? '').trim();
      const selectedType = String(selection.contextType ?? '').toUpperCase();
      if (selectedId) {
        if (selectedType === 'FOLDER') next.folderId = selectedId;
        if (selectedType === 'PROJECT') next.projectId = selectedId;
        if (selectedType === 'ACTIVITY') next.activityId = selectedId;
      }

      return next;
    },
    [],
  );

  const handleContextSelectionChange = useCallback(
    (selection: ContextObject | null) => {
      setIssueContextSelection(selection);
      const nextIds = getContextIdsFromSelection(selection);
      setContextIds(nextIds);
    },
    [getContextIdsFromSelection],
  );

  const contextSelectionDefaultValue = useMemo(() => {
    const raw =
      contextIds.activityId ||
      contextIds.projectId ||
      contextIds.folderId ||
      context?.activityId ||
      context?.projectId ||
      context?.folderId ||
      '';
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [
    context?.activityId,
    context?.folderId,
    context?.projectId,
    contextIds.activityId,
    contextIds.folderId,
    contextIds.projectId,
  ]);

  const onContextPathResolveError = useCallback((error: unknown) => {
    console.error('Issue context resolve failed', error);
  }, []);
  const defaultStatusId = useMemo(() => {
    const templateStatus = String(template.status || '').trim();
    if (templateStatus) {
      return templateStatus;
    }

    const normalizedStatusList = (statusList || []).filter((item) => String(item?.title || '').trim() !== '');
    if (normalizedStatusList.length === 0) {
      return '';
    }

    const preferredStatus = normalizedStatusList.find((item) =>
      ['počátek', 'pocatek', 'initial', 'start'].includes(String(item.title || '').trim().toLowerCase())
    );

    return String(preferredStatus?.ident || normalizedStatusList[0]?.ident || '');
  }, [statusList, template.status]);

  const buildCreatePayload = () => {
    const now = moment();
    const dateFrom = now.clone().seconds(0).milliseconds(0);
    const dateTo = dateFrom.clone().add(2, 'hours');
    const solverId = String(template.solver || store.UserId || '').trim();
    const notifyUserId = String(sendTo || store.UserId || '').trim();

    return {
      chargeable: '0',
      remoteHeadId: '0',
      remoteId: '0',
      resourceId: '0',
      status: '0',
      userId: solverId || String(store.UserId || ''),
      completed: undefined,
      multiEventTask: '0',
      forUser: String(store.UserId || ''),
      location: '',
      iversion: '',
      solutionStatus: defaultStatusId,
      developmentStatus: '0',
      severity: '0',
      isPrivate: '0',
      activityId: String(contextIds.activityId || ''),
      projectId: String(contextIds.projectId || ''),
      folderId: String(contextIds.folderId || ''),
      nodeId: '0',
      customId: '0',
      comments: '',
      reminder: String(template.reminder || '0'),
      addDuration: '0',
      estimationHours: '0',
      actualHours: '0',
      remainingHours: '0',
      plannedTaskCurrent: '0',
      milestones: '0',
      description: '',
      htmlText: '',
      dateTo: dateTo.format('YYYY-MM-DDTHH:mm:ss'),
      dateFrom: dateFrom.format('YYYY-MM-DDTHH:mm:ss'),
      priority: String(template.priority || '0'),
      entryType: String(template.typeOfWork || ''),
      distributionHandle: undefined,
      aclh: undefined,
      headId: '0',
      headUserId: solverId || String(store.UserId || ''),
      title: displayTitle,
      ident: undefined,
      dimension1: '0',
      dimension2: '0',
      dimension3: '0',
      taskTemplateId: undefined,
      USER: { id: solverId || String(store.UserId || '') },
      GARANT: notifyUserId ? [{ userId: notifyUserId }] : [],
      REPEAT: template.repeat
        ? {
            repeatMode: template.repeat.repeatMode || '2',
            validFrom: template.repeat.validFrom || dateFrom.format('YYYY-MM-DDTHH:mm:ss'),
            validTo: template.repeat.validTo || dateTo.clone().add(30, 'days').startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
            repeatEvery: template.repeat.repeatEvery || '1',
            repeatDay: template.repeat.repeatDay || '0',
            repeatMonth: template.repeat.repeatMonth || '0',
            repeatWeekDay: template.repeat.repeatWeekDay || '0',
            repeatWeekCount: template.repeat.repeatWeekCount || '0',
            repeatWeekDayList: template.repeat.repeatWeekDayList || '0',
            timeFrom: template.repeat.timeFrom || dateFrom.format('HH:mm'),
            timeTo: template.repeat.timeTo || dateTo.format('HH:mm'),
            useOffset: template.repeat.useOffset || '0',
            offset: template.repeat.offset || '0',
            limit: template.repeat.limit || '1',
          }
        : undefined,
      notify: '1',
      refId: docNo.trim(),
    };
  };

  const handleCreate = async () => {
    if (isSaving) return;

    setIsSaving(true);
    try {
      const createPayload = buildCreatePayload();
      const createResponse: any = await schedulerService.createIssue(
        new CreateIssueRequest(store.Server, store.SessionId, createPayload)
      );

      if (createResponse?.result !== 'OK' || !createResponse?.newId) {
        ToastQueue.negative(createResponse?.EXCEPTION?.message || _m('unable_to_create_issue'), { timeout: 2000 });
        return;
      }

      const createdIssueId = String(createResponse.newId);
      let createdIssueResponse: any = null;
      try {
        createdIssueResponse = await schedulerService.getIssue(
          new GetIssueRequest(store.Server, store.SessionId, createdIssueId, String(store.UserId || ''))
        );
      } catch (error) {
        createdIssueResponse = null;
      }

      const createdDetail = createdIssueResponse?.ITEM || {};
      if (pendingDocuments.length > 0) {
        const issueUpdatePayload = {
          ...(createdDetail?.ident ? createdDetail : createPayload),
          ident: createdIssueId,
          headId: String(createdDetail?.headId || createResponse?.headId || createdIssueId),
          DOCUMENT: pendingDocuments.map((document) => ({
            documentId: String(document.documentId),
            name: document.name,
          })),
        };

        const documentResponse: any = await schedulerService.updateIssue(
          new UpdateIssueRequest(store.Server, store.SessionId, issueUpdatePayload)
        );

        if (documentResponse?.result !== 'OK') {
          ToastQueue.negative(documentResponse?.EXCEPTION?.message || _m('unable_to_upload_document'), { timeout: 2000 });
        }
      }

      const formSaved = formId
        ? await customFormRef.current?.submitForm({
            remoteId: createdIssueId,
            remoteAcl: String(createdDetail?.aclh || ''),
          })
        : true;

      if (formSaved === false) {
        ToastQueue.negative(_m('issue_created_but_form_values_not_saved'), { timeout: 2500 });
      } else {
        ToastQueue.positive(_m('issue_created'), { timeout: 1800 });
      }

      await onCreated?.({ ident: createdIssueId });
      onClose();
    } catch (error: any) {
      ToastQueue.negative(error?.message || _m('unable_to_create_issue'), { timeout: 2000 });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Dialog
        width={{ base: '100%', M: '1100px' }}
        aria-label={t('issue_template_picker.new_issue', { ns: 'issue' })}
        isDismissable
        onDismiss={onClose}
      >
        <Heading level={2}>{displayTitle}</Heading>
        <Content UNSAFE_className={styles.dialogContent}>
          <Flex direction="column" height="100%" minHeight="0">
            <Flex direction="column" UNSAFE_className={styles.requestPanel}>
              <Flex direction="column" UNSAFE_className={styles.requestScroll}>
                <View width="100%" UNSAFE_className={styles.requestContentWidth}>
                  {formId ? (
                    <IssueCustomFormView
                      ref={customFormRef}
                      formId={formId}
                    />
                  ) : (
                    <View UNSAFE_className={styles.emptyState}>
                      {_m('no_custom_form_for_template')}
                    </View>
                  )}
                </View>
              </Flex>
            </Flex>
          </Flex>
        </Content>
      </Dialog>

      <Flex UNSAFE_className={styles.footerBar}>
        <Flex alignItems="end" gap="size-300" flex={1} wrap>
          <View flex={1} minWidth="220px">
            <Text UNSAFE_className={styles.footerLabel}>{_t('context')}:</Text>
            <View UNSAFE_className={styles.footerField}>
              <ContextSelection
                value={issueContextSelection}
                onSelectionChange={handleContextSelectionChange}
                folderStatusMap={folderStatusMapping}
                projectStatusMap={projectStatusMapping}
                activityStatusMap={activityStatusMapping}
                defaultValue={contextSelectionDefaultValue}
                onExplicitContextResolveError={onContextPathResolveError}
                allowClearSelection
                allowTransparentBG
                filledBackground={false}
                showBorders={false}
                aria-label={_t('context')}
              />
            </View>
          </View>
          <View flex={1} minWidth="220px">
            <TextField
              aria-label={_t('document_no_optional')}
              label={_t('document_no_optional')}
              value={docNo}
              onChange={setDocNo}
              width="100%"
            />
          </View>
          <View flex={1} minWidth="220px">
            <Text UNSAFE_className={styles.footerLabel}>{_t('send_to')}:</Text>
            <View UNSAFE_className={styles.footerField}>
              <Picker
                aria-label={_t('send_to')}
                items={sendToOptions}
                selectedKey={sendTo}
                onSelectionChange={(key) => setSendTo(key ? String(key) : undefined)}
                placeholder={_t('select_user')}
                isQuiet
                isDisabled={sendToOptions.length === 0}
                width="100%"
              >
                {(item) => <Item key={item.id}>{item.name}</Item>}
              </Picker>
            </View>
          </View>
        </Flex>
        <Flex UNSAFE_className={styles.footerActions}>
          <ActionButton
            UNSAFE_className={styles.uploadLink}
            onPress={() => setShowUploadDialog(true)}
            isDisabled={isSaving}
          >
            <Upload size={16} />
            <span>{t('Add attachment', { ns: 'issue', defaultValue: 'Add attachment' })}</span>
            {pendingDocuments.length > 0 ? (
              <span className={styles.uploadCount}>{pendingDocuments.length}</span>
            ) : null}
          </ActionButton>

          <ActionButton
            UNSAFE_className={styles.saveButton}
            onPress={handleCreate}
            isDisabled={isSaving}
          >
            {isSaving ? (
              <ProgressCircle aria-label="Saving issue" size="S" isIndeterminate />
            ) : (
              <>
                <span className={styles.saveButtonIcon}>+</span>
                <span>{_tt('button_save')}</span>
              </>
            )}
          </ActionButton>
        </Flex>
      </Flex>

      <CreateIssueDocumentUploadDialog
        isOpen={showUploadDialog}
        context={{
          folderId: contextIds.folderId,
          projectId: contextIds.projectId,
          activityId: contextIds.activityId,
        }}
        onClose={() => setShowUploadDialog(false)}
        onUploaded={async (documents) => {
          setPendingDocuments((prev) => {
            const merged = [...prev];
            documents.forEach((document) => {
              const existingIndex = merged.findIndex((item) => String(item.documentId) === String(document.documentId));
              if (existingIndex >= 0) {
                merged[existingIndex] = document;
              } else {
                merged.push(document);
              }
            });
            return merged;
          });
        }}
      />
    </>
  );
};

export default CreateIssueDialog;
